import { createFileRoute } from "../lib/router-compat";
import { PanBackdrop } from "../components/PageShell";

export const Route = createFileRoute("/logos")({
  head: () => ({
    meta: [
      { title: "Logo Variations — Pan Institute" },
      { name: "description", content: "Logo explorations for Pan Institute." },
    ],
  }),
  component: LogosPage,
});

/* ---------- Icon primitives ---------- */

// Concentric pan drum (top-down)
function PanDrum({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden>
      <circle cx="32" cy="32" r="30" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="32" cy="32" r="22" fill="none" stroke="currentColor" strokeWidth="1.25" opacity="0.7" />
      <ellipse cx="32" cy="20" rx="6" ry="4" fill="none" stroke="currentColor" strokeWidth="1.25" />
      <ellipse cx="20" cy="34" rx="5" ry="3.5" fill="none" stroke="currentColor" strokeWidth="1.25" />
      <ellipse cx="44" cy="34" rx="5" ry="3.5" fill="none" stroke="currentColor" strokeWidth="1.25" />
      <ellipse cx="32" cy="46" rx="6" ry="4" fill="none" stroke="currentColor" strokeWidth="1.25" />
    </svg>
  );
}

// Sound-wave arcs forming a "P"
function PanWaves({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden>
      <path d="M14 50 V14 H36 a12 12 0 0 1 0 24 H22" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      <path d="M44 22 a14 14 0 0 1 0 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.7" />
      <path d="M50 18 a20 20 0 0 1 0 28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.4" />
    </svg>
  );
}

// Stick + drum mark
function PanStick({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden>
      <circle cx="28" cy="36" r="20" fill="none" stroke="currentColor" strokeWidth="2.5" />
      <circle cx="28" cy="36" r="12" fill="none" stroke="currentColor" strokeWidth="1.25" opacity="0.6" />
      <line x1="44" y1="20" x2="58" y2="6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="58" cy="6" r="3" fill="currentColor" />
    </svg>
  );
}

// Solid filled disc with notch
function PanDisc({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden>
      <circle cx="32" cy="32" r="28" fill="currentColor" />
      <circle cx="32" cy="32" r="20" fill="none" stroke="var(--background)" strokeWidth="1.5" opacity="0.6" />
      <circle cx="32" cy="14" r="3" fill="var(--background)" />
    </svg>
  );
}

// Tenor pan — radial note ovals around a center
function PanTenor({ className = "" }: { className?: string }) {
  const notes = Array.from({ length: 8 }, (_, i) => {
    const a = (i / 8) * Math.PI * 2 - Math.PI / 2;
    const r = 20;
    const cx = 32 + Math.cos(a) * r;
    const cy = 32 + Math.sin(a) * r;
    const rot = (a * 180) / Math.PI + 90;
    return (
      <ellipse
        key={i}
        cx={cx}
        cy={cy}
        rx="5.5"
        ry="3.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.25"
        transform={`rotate(${rot} ${cx} ${cy})`}
      />
    );
  });
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden>
      <circle cx="32" cy="32" r="30" fill="none" stroke="currentColor" strokeWidth="2" />
      {notes}
      <circle cx="32" cy="32" r="4" fill="currentColor" />
    </svg>
  );
}

// Bass pan — three large oval notes, deep dish
function PanBass({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden>
      <circle cx="32" cy="32" r="30" fill="none" stroke="currentColor" strokeWidth="2.5" />
      <circle cx="32" cy="32" r="24" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.4" />
      <ellipse cx="32" cy="18" rx="9" ry="6" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <ellipse cx="20" cy="40" rx="9" ry="6" fill="none" stroke="currentColor" strokeWidth="1.5" transform="rotate(-30 20 40)" />
      <ellipse cx="44" cy="40" rx="9" ry="6" fill="none" stroke="currentColor" strokeWidth="1.5" transform="rotate(30 44 40)" />
    </svg>
  );
}

// Side profile — pan on stand with strap
function PanProfile({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden>
      <ellipse cx="32" cy="26" rx="24" ry="6" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M8 26 V38 a24 8 0 0 0 48 0 V26" fill="none" stroke="currentColor" strokeWidth="2" />
      <line x1="14" y1="44" x2="14" y2="58" stroke="currentColor" strokeWidth="1.5" />
      <line x1="50" y1="44" x2="50" y2="58" stroke="currentColor" strokeWidth="1.5" />
      <line x1="10" y1="58" x2="54" y2="58" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

// Crossed sticks over disc
function PanCrossed({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden>
      <circle cx="32" cy="34" r="22" fill="none" stroke="currentColor" strokeWidth="2" />
      <line x1="10" y1="10" x2="54" y2="54" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="54" y1="10" x2="10" y2="54" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="10" cy="10" r="3" fill="currentColor" />
      <circle cx="54" cy="10" r="3" fill="currentColor" />
    </svg>
  );
}

// Trio of pans — institute mark
function PanTrio({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden>
      <circle cx="20" cy="40" r="14" fill="none" stroke="currentColor" strokeWidth="1.75" />
      <circle cx="44" cy="40" r="14" fill="none" stroke="currentColor" strokeWidth="1.75" />
      <circle cx="32" cy="22" r="14" fill="currentColor" stroke="currentColor" strokeWidth="1.75" />
      <circle cx="32" cy="22" r="8" fill="var(--background)" opacity="0.9" />
    </svg>
  );
}

// Ripple — single note struck, radiating
function PanRipple({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden>
      <circle cx="32" cy="32" r="4" fill="currentColor" />
      <circle cx="32" cy="32" r="11" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.85" />
      <circle cx="32" cy="32" r="19" fill="none" stroke="currentColor" strokeWidth="1.25" opacity="0.55" />
      <circle cx="32" cy="32" r="27" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.3" />
    </svg>
  );
}

// Monogram PI
function MonogramPI({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden>
      <rect x="2" y="2" width="60" height="60" rx="6" fill="none" stroke="currentColor" strokeWidth="2" />
      <text
        x="32"
        y="42"
        textAnchor="middle"
        fontFamily="Georgia, serif"
        fontSize="30"
        fontWeight="700"
        fill="currentColor"
        letterSpacing="-1"
      >
        PI
      </text>
    </svg>
  );
}

/* ---------- Logo lockups ---------- */

function Tile({
  title,
  dark = false,
  children,
}: {
  title: string;
  dark?: boolean;
  children: React.ReactNode;
}) {
  return (
    <figure className="flex flex-col">
      <div
        className={`flex aspect-[4/3] items-center justify-center rounded-2xl border p-10 ${
          dark
            ? "bg-foreground text-background border-foreground"
            : "bg-card text-foreground border-border"
        }`}
      >
        {children}
      </div>
      <figcaption className="mt-3 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
        {title}
      </figcaption>
    </figure>
  );
}

function Wordmark({
  size = "text-3xl",
  tracking = "tracking-[0.32em]",
  weight = "font-light",
}: {
  size?: string;
  tracking?: string;
  weight?: string;
}) {
  return (
    <span className={`font-display uppercase ${size} ${tracking} ${weight}`}>
      Pan Institute
    </span>
  );
}

function LogosPage() {
  return (
    <div className="min-h-screen text-foreground">
      <PanBackdrop />
      <header className="border-b border-border">
        <div className="mx-auto max-w-7xl px-6 py-10">
          <p className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
            Brand exploration
          </p>
          <h1 className="mt-2 font-display text-3xl md:text-5xl">Pan Institute — Logo Variations</h1>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
            A range of marks: icon + wordmark lockups, wordmark only, monogram, and stacked
            arrangements. Built with semantic tokens so every variation works on light and dark.
          </p>
        </div>
      </header>

      {/* Hero — primary mark */}
      <section className="bg-foreground">
        <div className="mx-auto flex max-w-7xl items-center justify-center px-6 py-32">
          <span className="font-display text-5xl md:text-7xl uppercase tracking-[0.1em] font-bold text-primary">
            Pan Institute
          </span>
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-6 py-12 space-y-16">

        {/* Section: New pan icons */}
        <section>
          <h2 className="mb-6 text-xs uppercase tracking-[0.25em] text-muted-foreground">
            Pan-inspired icons
          </h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <Tile title="Tenor — radial notes">
              <PanTenor className="h-24 w-24" />
            </Tile>
            <Tile title="Bass — three notes" dark>
              <PanBass className="h-24 w-24" />
            </Tile>
            <Tile title="Profile on stand">
              <PanProfile className="h-24 w-24" />
            </Tile>
            <Tile title="Crossed sticks" dark>
              <PanCrossed className="h-24 w-24" />
            </Tile>
            <Tile title="Trio — institute">
              <PanTrio className="h-24 w-24" />
            </Tile>
            <Tile title="Ripple — struck note" dark>
              <PanRipple className="h-24 w-24" />
            </Tile>
            <Tile title="Tenor + wordmark">
              <div className="flex items-center gap-4">
                <PanTenor className="h-12 w-12" />
                <Wordmark size="text-2xl" />
              </div>
            </Tile>
            <Tile title="Trio + stacked" dark>
              <div className="flex flex-col items-center gap-3">
                <PanTrio className="h-14 w-14" />
                <Wordmark size="text-xl" tracking="tracking-[0.4em]" />
              </div>
            </Tile>
          </div>
        </section>

        {/* Section: With icon */}
        <section>

          <h2 className="mb-6 text-xs uppercase tracking-[0.25em] text-muted-foreground">
            With icon
          </h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <Tile title="Drum + horizontal">
              <div className="flex items-center gap-4">
                <PanDrum className="h-12 w-12" />
                <Wordmark />
              </div>
            </Tile>

            <Tile title="Waves + horizontal" dark>
              <div className="flex items-center gap-4">
                <PanWaves className="h-12 w-12" />
                <Wordmark />
              </div>
            </Tile>

            <Tile title="Stick mark + horizontal">
              <div className="flex items-center gap-4">
                <PanStick className="h-12 w-12" />
                <Wordmark />
              </div>
            </Tile>

            <Tile title="Disc + stacked" dark>
              <div className="flex flex-col items-center gap-4">
                <PanDisc className="h-16 w-16" />
                <Wordmark size="text-2xl" />
              </div>
            </Tile>

            <Tile title="Drum + stacked, two-line">
              <div className="flex flex-col items-center gap-3">
                <PanDrum className="h-14 w-14" />
                <div className="flex flex-col items-center leading-none">
                  <span className="font-display text-3xl tracking-[0.35em] uppercase font-light">
                    Pan
                  </span>
                  <span className="font-display text-xs tracking-[0.55em] uppercase mt-2 text-muted-foreground">
                    Institute
                  </span>
                </div>
              </div>
            </Tile>

            <Tile title="Monogram + horizontal" dark>
              <div className="flex items-center gap-4">
                <MonogramPI className="h-12 w-12" />
                <Wordmark />
              </div>
            </Tile>
          </div>
        </section>

        {/* Section: Wordmark only */}
        <section>
          <h2 className="mb-6 text-xs uppercase tracking-[0.25em] text-muted-foreground">
            Wordmark only
          </h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <Tile title="Light, wide tracking">
              <Wordmark size="text-3xl" tracking="tracking-[0.4em]" weight="font-light" />
            </Tile>

            <Tile title="Bold, tight tracking" dark>
              <Wordmark size="text-3xl" tracking="tracking-[0.1em]" weight="font-bold" />
            </Tile>

            <Tile title="Two-line stack">
              <div className="flex flex-col items-center leading-[0.9]">
                <span className="font-display text-5xl tracking-tight uppercase font-semibold">
                  Pan
                </span>
                <span className="font-display text-5xl tracking-tight uppercase font-light text-muted-foreground">
                  Institute
                </span>
              </div>
            </Tile>

            <Tile title="With underline rule" dark>
              <div className="flex flex-col items-center">
                <Wordmark size="text-2xl" tracking="tracking-[0.45em]" />
                <span className="mt-3 block h-px w-24 bg-background/60" />
                <span className="mt-3 text-[10px] uppercase tracking-[0.4em] opacity-70">
                  Est. 2014
                </span>
              </div>
            </Tile>

            <Tile title="All caps, condensed">
              <span className="font-display text-4xl uppercase font-black tracking-[-0.02em]">
                PANINSTITUTE
              </span>
            </Tile>

            <Tile title="Serif italic">
              <span className="font-display text-4xl italic">
                Pan <span className="font-light">Institute</span>
              </span>
            </Tile>
          </div>
        </section>

        {/* Section: Monograms / badges */}
        <section>
          <h2 className="mb-6 text-xs uppercase tracking-[0.25em] text-muted-foreground">
            Monograms & badges
          </h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <Tile title="PI mono — light">
              <MonogramPI className="h-24 w-24" />
            </Tile>
            <Tile title="PI mono — dark" dark>
              <MonogramPI className="h-24 w-24" />
            </Tile>
            <Tile title="Disc mark">
              <PanDisc className="h-24 w-24" />
            </Tile>
            <Tile title="Circular badge" dark>
              <div className="relative flex h-32 w-32 items-center justify-center rounded-full border border-background/40">
                <span className="absolute inset-2 rounded-full border border-background/20" />
                <div className="flex flex-col items-center leading-none">
                  <span className="font-display text-xl tracking-[0.25em] uppercase">Pan</span>
                  <span className="mt-1 text-[9px] tracking-[0.4em] uppercase opacity-70">
                    Institute
                  </span>
                </div>
              </div>
            </Tile>
          </div>
        </section>
      </main>

      <footer className="border-t border-border">
        <div className="mx-auto max-w-7xl px-6 py-8 text-xs uppercase tracking-[0.25em] text-muted-foreground">
          /logos — exploration
        </div>
      </footer>
    </div>
  );
}
